import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, Chip, Stack, Button,
  IconButton, Tooltip, Avatar, Drawer, Divider, Grid, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PetsIcon from '@mui/icons-material/Pets';
import BiotechIcon from '@mui/icons-material/Biotech';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import GridViewIcon from '@mui/icons-material/GridView';
import TableRowsIcon from '@mui/icons-material/TableRows';
import DownloadIcon from '@mui/icons-material/Download';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import * as XLSX from 'xlsx';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClearIcon from '@mui/icons-material/Clear';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import PatientOwnerDialog from '../components/PatientOwnerDialog';
import { db } from '../firebase';
import { COLORS } from '../theme/constants';
import { useBranch } from '../context/BranchContext';

// ── Helpers ────────────────────────────────────────────────────────────────
function calcAge(birthDate) {
  if (!birthDate) return '—';
  const b = new Date(birthDate);
  const now = new Date();
  const years = Math.floor((now - b) / (365.25 * 864e5));
  if (years >= 1) return `${years} año${years !== 1 ? 's' : ''}`;
  const months = Math.floor((now - b) / (30.44 * 864e5));
  if (months >= 1) return `${months} mes${months !== 1 ? 'es' : ''}`;
  return '< 1 mes';
}

const SPECIES_COLOR = {
  canino: { bg: '#FFF3E0', text: '#E65100', avatar: '#FF9800' },
  felino: { bg: '#F3E5F5', text: '#6A1B9A', avatar: '#AB47BC' },
};
function speciesColor(s = '') {
  return SPECIES_COLOR[(s || '').toLowerCase()] || { bg: '#E3F2FD', text: '#1565C0', avatar: '#42A5F5' };
}

const { primary: PRIMARY, teal: TEAL, warning: AMBER, danger: DANGER, success: SUCCESS } = COLORS;



// ── Drawer de perfil de paciente ───────────────────────────────────────────
function PatientDrawer({ patient, onClose, onEdit }) {
  const navigate = useNavigate();
  const [labCount, setLabCount] = useState(null);

  useEffect(() => {
    if (!patient) return;
    getDocs(query(collection(db, 'labOrders'), where('patientId', '==', patient.patientId)))
      .then(s => setLabCount(s.size))
      .catch(() => setLabCount(0));
  }, [patient]);

  if (!patient) return null;
  const sc = speciesColor(patient.species);

  return (
    <Drawer anchor="right" open={!!patient} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>Ficha del Paciente</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ p: 3, overflowY: 'auto' }}>
        {/* Alertas médicas */}
        {(patient.alergias || patient.condicionesCronicas || patient.isHospitalized) && (
          <Box sx={{ mb: 2 }}>
            {patient.isHospitalized && (
              <Alert severity="info" sx={{ mb: 1, py: 0.5 }}
                action={<Button size="small" onClick={() => navigate('/hospital')}>Ver</Button>}>
                Actualmente internado/a
              </Alert>
            )}
            {patient.alergias && (
              <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
                <strong>Alergias:</strong> {patient.alergias}
              </Alert>
            )}
            {patient.condicionesCronicas && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <strong>Condiciones:</strong> {patient.condicionesCronicas}
              </Alert>
            )}
          </Box>
        )}

        {/* Avatar + nombre */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            src={patient.photoURL}
            sx={{ width: 64, height: 64, bgcolor: sc.avatar, fontSize: 26, fontWeight: 700 }}>
            {!patient.photoURL && (patient.name?.[0]?.toUpperCase() || <PetsIcon />)}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700}>{patient.name}</Typography>
            <Chip label={patient.species || 'Sin especie'} size="small" sx={{ bgcolor: sc.bg, color: sc.text, fontWeight: 600, mt: 0.5 }} />
          </Box>
        </Box>

        {/* Datos clínicos */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>DATOS CLÍNICOS</Typography>
          <Grid container spacing={1}>
            {[
              ['Raza',          patient.breed || '—'],
              ['Sexo',          patient.genero || '—'],
              ['Edad',          calcAge(patient.birthDate)],
              ['Peso',          patient.weight ? `${patient.weight} kg` : '—'],
              ['Microchip',     patient.microchip || '—'],
              ['Esterilizado',  patient.esterilizado ? 'Sí' : 'No'],
              ['Grupo sanguíneo', patient.grupoSanguineo || '—'],
              ['Convenio',      patient.ownerConvenio || '—'],
            ].map(([label, val]) => (
              <React.Fragment key={label}>
                <Grid size={5}><Typography variant="body2" color="text.secondary">{label}</Typography></Grid>
                <Grid size={7}><Typography variant="body2" fontWeight={500}>{val}</Typography></Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Paper>

        {/* Datos propietario */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>PROPIETARIO</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight={600}>{patient.ownerName || '—'}</Typography>
          </Box>
          {patient.ownerCelular && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2">{patient.ownerCelular}</Typography>
            </Box>
          )}
          {patient.ownerContact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon fontSize="small" color="action" />
              <Typography variant="body2">{patient.ownerContact}</Typography>
            </Box>
          )}
          {patient.ownerRut && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>RUT: {patient.ownerRut}</Typography>
          )}
          {patient.ownerComuna && (
            <Typography variant="body2" color="text.secondary">Comuna: {patient.ownerComuna}</Typography>
          )}
        </Paper>

        {/* Laboratorio */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>LABORATORIO</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BiotechIcon color="action" fontSize="small" />
              <Typography variant="body2">
                {labCount === null ? 'Consultando...' : `${labCount} orden${labCount !== 1 ? 'es' : ''} de laboratorio`}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" onClick={() => navigate(`/lab/history?patientId=${patient.patientId}`)}>
              Ver historial
            </Button>
          </Box>
        </Paper>

        {/* Acciones rápidas */}
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button fullWidth variant="contained" startIcon={<MedicalServicesIcon />}
            onClick={() => navigate(`/patients/${patient.patientId}/record`)}
            sx={{ bgcolor: PRIMARY, fontWeight: 700 }}>
            Ver Ficha Clínica
          </Button>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<CalendarTodayIcon />}
              onClick={() => navigate('/scheduler', { state: { patientData: patient } })}>
              Agendar cita
            </Button>
            <Button variant="outlined" size="small" startIcon={<MedicalServicesIcon />}
              onClick={() => navigate('/surgery-scheduler', { state: { patientData: patient } })}>
              Programar cirugía
            </Button>
          </Box>
          {patient.ownerCelular && (
            <Button variant="outlined" size="small" color="success" startIcon={<WhatsAppIcon />}
              component="a"
              href={`https://wa.me/56${patient.ownerCelular.replace(/\D/g, '')}`}
              target="_blank">
              WhatsApp al propietario
            </Button>
          )}
          <Button variant="outlined" size="small" startIcon={<EditIcon />}
            onClick={() => { onEdit(patient); onClose(); }}>
            Editar datos
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

const BOOKING_STATUS_LABELS = {
  confirmed: 'Confirmado', pending: 'Pendiente', en_sala: 'En sala', completed: 'Atendido', cancelled: 'Cancelado', no_show: 'No asistió'
};

// ── Diálogo de historial de citas ───────────────────────────────────────────
function BookingsHistoryDialog({ patient, onClose }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patient) return;
    setLoading(true);
    Promise.all([
      getDocs(query(collection(db, 'onlineBookings'), where('patientId', '==', patient.patientId))),
      getDocs(query(collection(db, 'onlineBookings'), where('petId', '==', patient.patientId))),
    ]).then(([snap1, snap2]) => {
      const map = new Map();
      [...snap1.docs, ...snap2.docs].forEach((d) => {
        map.set(d.id, { id: d.id, ...d.data() });
      });
      const bks = Array.from(map.values()).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      setBookings(bks);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [patient]);

  if (!patient) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Historial de citas — {patient.name}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : bookings.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>No hay citas registradas para este paciente.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Hora</TableCell>
                  <TableCell>Profesional</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>{b.date ? new Date(b.date + 'T00:00:00').toLocaleDateString('es-CL') : '—'}</TableCell>
                    <TableCell>{b.time || '—'}</TableCell>
                    <TableCell>{b.professionalName || b.professionalId || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={BOOKING_STATUS_LABELS[b.status] || b.status || '—'}
                        size="small"
                        sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" size="small">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Página principal ───────────────────────────────────────────────────────
const Patients = () => {
  const navigate = useNavigate();
  const { filterByBranch, activeBranchId } = useBranch();
  const [patients, setPatients]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [search, setSearch]           = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('Todos');
  const [sexFilter, setSexFilter]     = useState('Todos');
  const [ageFilter, setAgeFilter]     = useState('Todos');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [sortColumn, setSortColumn]   = useState('name');
  const [sortDir, setSortDir]         = useState('asc');
  const [viewMode, setViewMode]       = useState('table');
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [drawerPatient, setDrawerPatient] = useState(null);
  const [editTarget, setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [newOpen, setNewOpen]         = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [snackbar, setSnackbar]       = useState({ open: false, message: '', severity: 'success' });
  const [historyPatient, setHistoryPatient] = useState(null);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const [pSnap, oSnap] = await Promise.all([
        getDocs(collection(db, 'patients')),
        getDocs(collection(db, 'owners')),
      ]);
      const ownersMap = {};
      oSnap.forEach(d => { ownersMap[d.id] = { id: d.id, ...d.data() }; });

      const list = pSnap.docs.map(d => {
        const p = { id: d.id, ...d.data() };
        const o = ownersMap[p.ownerId] || {};
        return {
          patientId:    p.id,
          ficha:        p.ficha || '',
          name:         p.name || '',
          species:      p.species || '',
          breed:        p.breed || '',
          genero:       p.genero || '',
          birthDate:    p.birthDate || '',
          ownerId:      p.ownerId || '',
          ownerName:    o.name     || '—',
          ownerRut:     o.rut      || '',
          ownerCelular: o.celular  || '',
          ownerMail:    o.mail || o.contact || '',
          ownerContact: o.mail || o.contact || '',
          ownerRegion:  o.region   || '',
          ownerComuna:  o.comuna   || '',
          ownerConvenio: o.convenio || '',
          photoURL:     p.photoURL || '',
          weight:       p.weight || '',
          microchip:    p.microchip || '',
          esterilizado: p.esterilizado || false,
          grupoSanguineo: p.grupoSanguineo || '',
          alergias:     p.alergias || '',
          condicionesCronicas: p.condicionesCronicas || '',
          isHospitalized: p.status === 'hospitalizado',
          isDeceased:   p.estado_vital === 'FALLECIDO',
          estado_vital: p.estado_vital || 'VIVO',
          status:       p.status || 'activo',
          branchIds:    p.branchIds || [],
        };
      });
      setPatients(list);
    } catch (err) {
      setError('Error al cargar los pacientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);

  const speciesOptions = useMemo(() => {
    const set = new Set(patients.map(p => p.species).filter(Boolean));
    return ['Todos', ...Array.from(set).sort()];
  }, [patients]);

  const filteredByBranch = useMemo(
    () => filterByBranch(patients, { includeUnassigned: true }),
    [patients, filterByBranch, activeBranchId]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return filteredByBranch.filter(p => {
      const matchSearch = !q ||
        p.name.toLowerCase().includes(q) ||
        p.ownerName.toLowerCase().includes(q) ||
        p.breed.toLowerCase().includes(q) ||
        (p.ownerRut || '').toLowerCase().includes(q) ||
        (p.microchip || '').toLowerCase().includes(q) ||
        (p.ownerCelular || '').includes(q) ||
        (p.ownerConvenio || '').toLowerCase().includes(q);

      const matchSpecies = speciesFilter === 'Todos' || p.species === speciesFilter;
      const matchSex = sexFilter === 'Todos' || p.genero === sexFilter;
      const matchStatus = statusFilter === 'Todos'
        ? p.estado_vital !== 'FALLECIDO'
        : statusFilter === 'internados'
          ? p.isHospitalized
          : statusFilter === 'fallecidos'
            ? p.estado_vital === 'FALLECIDO'
            : (p.status || 'activo') === statusFilter;

      const matchAge = ageFilter === 'Todos' || (() => {
        if (!p.birthDate) return false;
        const years = Math.floor((new Date() - new Date(p.birthDate)) / (365.25 * 864e5));
        if (ageFilter === 'cachorro') return years < 1;
        if (ageFilter === 'joven') return years >= 1 && years < 3;
        if (ageFilter === 'adulto') return years >= 3 && years < 8;
        if (ageFilter === 'senior') return years >= 8;
        return true;
      })();

      return matchSearch && matchSpecies && matchSex && matchStatus && matchAge;
    });
  }, [filteredByBranch, search, speciesFilter, sexFilter, statusFilter, ageFilter]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortColumn) {
        case 'name':    return dir * a.name.localeCompare(b.name, 'es');
        case 'species': return dir * (a.species || '').localeCompare(b.species || '', 'es');
        case 'breed':   return dir * (a.breed || '').localeCompare(b.breed || '', 'es');
        case 'age':     return dir * ((a.birthDate || '') < (b.birthDate || '') ? 1 : -1);
        case 'owner':   return dir * (a.ownerName || '').localeCompare(b.ownerName || '', 'es');
        default:        return 0;
      }
    });
  }, [filtered, sortColumn, sortDir]);

  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDir('asc');
    }
  };

  const handleArchive = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await updateDoc(doc(db, 'patients', deleteTarget.patientId), {
        status: 'inactivo',
        inactivatedAt: new Date(),
      });
      setDeleteTarget(null);
      fetchPatients();
      setSnackbar({ open: true, message: `${deleteTarget.name} archivado/a.`, severity: 'info' });
    } catch {
      setSnackbar({ open: true, message: 'Error al archivar', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleExportCsv = () => {
    const headers = ['Nombre', 'Especie', 'Raza', 'Sexo', 'Fecha Nacimiento', 'Edad',
      'Microchip', 'Propietario', 'RUT', 'Teléfono', 'Email', 'Convenio'];
    const rows = sorted.map(p => [
      p.name, p.species, p.breed, p.genero,
      p.birthDate, calcAge(p.birthDate),
      p.microchip || '',
      p.ownerName, p.ownerRut, p.ownerCelular, p.ownerMail, p.ownerConvenio || '',
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pacientes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    const rows = sorted.map((p) => ({
      pacientId: p.patientId || '',
      breed: p.breed || '',
      especie: p.species || '',
      ficha: p.ficha || '',
      genero: p.genero || '',
      name: p.name || '',
      ownerId: p.ownerId || '',
      rutTutor: p.ownerRut || '',
      edad: calcAge(p.birthDate),
      birthDate: p.birthDate || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Pacientes');
    XLSX.writeFile(wb, `pacientes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const SortableHeader = ({ col, label }) => (
    <TableCell
      sx={{ fontWeight: 700, cursor: 'pointer', userSelect: 'none',
        '&:hover': { bgcolor: '#F8FAFC' } }}
      onClick={() => handleSort(col)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        {sortColumn === col
          ? (sortDir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 14 }}/> : <ArrowDownwardIcon sx={{ fontSize: 14 }}/>)
          : <UnfoldMoreIcon sx={{ fontSize: 14, color: 'text.disabled' }}/>}
      </Box>
    </TableCell>
  );

  return (
    <Box>
      {/* Header con métricas */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Pacientes</Typography>
          <Typography variant="body2" color="text.secondary">
            {patients.filter(p => (!p.status || p.status === 'activo') && p.estado_vital !== 'FALLECIDO').length} pacientes activos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {[
            { label: 'Total', value: patients.length, color: 'text.primary' },
            { label: 'Internados', value: patients.filter(p => p.isHospitalized).length, color: '#F59E0B' },
            { label: 'Con alertas', value: patients.filter(p => p.alergias).length, color: '#DC2626' },
          ].map(({ label, value, color }) => (
            <Paper key={label} elevation={0} sx={{ px: 2, py: 1, border: '1px solid #E2E8F0', borderRadius: 2, textAlign: 'center', minWidth: 80 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1 }}>{value}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewOpen(true)}
            sx={{ bgcolor: PRIMARY, fontWeight: 700 }}>
            Nuevo Paciente
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filtros mejorados */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small" fullWidth
            placeholder="Buscar por nombre, propietario, RUT, microchip, teléfono…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small"/></IconButton>
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 400 }}
          />
          <ToggleButtonGroup value={viewMode} exclusive size="small"
            onChange={(_, v) => v && setViewMode(v)}>
            <ToggleButton value="table"><Tooltip title="Vista tabla"><TableRowsIcon fontSize="small"/></Tooltip></ToggleButton>
            <ToggleButton value="cards"><Tooltip title="Vista tarjetas"><GridViewIcon fontSize="small"/></Tooltip></ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Exportar a CSV">
            <IconButton size="small" onClick={handleExportCsv} disabled={sorted.length === 0}>
              <DownloadIcon fontSize="small"/>
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={sorted.length === 0}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Excel
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
            {sorted.length} paciente{sorted.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Especie:</Typography>
          {speciesOptions.map(s => (
            <Chip key={s} label={s} size="small" clickable
              variant={speciesFilter === s ? 'filled' : 'outlined'}
              color={speciesFilter === s ? 'primary' : 'default'}
              onClick={() => { setSpeciesFilter(s); setPage(0); }}
            />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }}/>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Sexo:</Typography>
          {['Todos', 'Macho', 'Hembra'].map(s => (
            <Chip key={s} label={s} size="small" clickable
              variant={sexFilter === s ? 'filled' : 'outlined'}
              color={sexFilter === s ? 'secondary' : 'default'}
              onClick={() => { setSexFilter(s); setPage(0); }}
            />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }}/>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Edad:</Typography>
          {[
            { val: 'Todos', label: 'Todos' },
            { val: 'cachorro', label: 'Cachorro (<1a)' },
            { val: 'joven', label: 'Joven (1-3a)' },
            { val: 'adulto', label: 'Adulto (3-8a)' },
            { val: 'senior', label: 'Senior (>8a)' },
          ].map(({ val, label }) => (
            <Chip key={val} label={label} size="small" clickable
              variant={ageFilter === val ? 'filled' : 'outlined'}
              color={ageFilter === val ? 'warning' : 'default'}
              onClick={() => { setAgeFilter(val); setPage(0); }}
            />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }}/>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Estado:</Typography>
          {[
            { val: 'Todos', label: 'Vivos', color: 'default' },
            { val: 'internados', label: 'Internados', color: 'warning' },
            { val: 'fallecidos', label: 'Fallecidos', color: 'error' },
          ].map(({ val, label, color }) => (
            <Chip key={val} label={label} size="small" clickable
              variant={statusFilter === val ? 'filled' : 'outlined'}
              color={statusFilter === val ? color : 'default'}
              onClick={() => { setStatusFilter(val); setPage(0); }}
            />
          ))}
          {(speciesFilter !== 'Todos' || sexFilter !== 'Todos' || ageFilter !== 'Todos' || statusFilter !== 'Todos' || search) && (
            <Chip label="✕ Limpiar" size="small" color="default" variant="outlined"
              onClick={() => {
                setSearch(''); setSpeciesFilter('Todos');
                setSexFilter('Todos'); setAgeFilter('Todos');
                setStatusFilter('Todos'); setPage(0);
              }}
              sx={{ ml: 'auto', cursor: 'pointer' }}
            />
          )}
        </Box>
      </Paper>

      {/* Tabla / Cards */}
      <Paper>
        {loading ? (
          <TableContainer>
            <Table size="small">
              <TableBody>
                {Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Skeleton variant="circular" width={32} height={32}/>
                        <Skeleton variant="text" width={100}/>
                      </Box>
                    </TableCell>
                    {[80, 70, 50, 50, 110, 60].map((w, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={w}/></TableCell>
                    ))}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {[0,1,2,3].map(j => <Skeleton key={j} variant="circular" width={24} height={24}/>)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : viewMode === 'table' ? (
          <>
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <SortableHeader col="name" label="Paciente"/>
                    <SortableHeader col="species" label="Especie"/>
                    <SortableHeader col="breed" label="Raza"/>
                    <SortableHeader col="age" label="Edad"/>
                    <TableCell sx={{ fontWeight: 700 }}>Sexo</TableCell>
                    <SortableHeader col="owner" label="Propietario"/>
                    <TableCell sx={{ fontWeight: 700 }}>Alertas</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No se encontraron pacientes
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((p) => {
                    const sc = speciesColor(p.species);
                    return (
                      <TableRow hover key={p.patientId} sx={{ cursor: 'pointer' }} onClick={() => setDrawerPatient(p)}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar src={p.photoURL}
                              sx={{ width: 32, height: 32, bgcolor: sc.avatar, fontSize: 14, fontWeight: 700 }}>
                              {!p.photoURL && (p.name?.[0]?.toUpperCase() || <PetsIcon sx={{ fontSize: 16 }} />)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={p.species || '—'} size="small" sx={{ bgcolor: sc.bg, color: sc.text, fontWeight: 600, fontSize: 11 }} />
                        </TableCell>
                        <TableCell><Typography variant="body2">{p.breed || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{calcAge(p.birthDate)}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{p.genero || '—'}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{p.ownerName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {p.isHospitalized && (
                              <Tooltip title="Actualmente internado">
                                <Chip label="🏥 Internado" size="small"
                                  sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontSize: '0.6rem', height: 20 }}/>
                              </Tooltip>
                            )}
                            {p.alergias && (
                              <Tooltip title={`Alergia: ${p.alergias}`}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DANGER,
                                  cursor: 'help', alignSelf: 'center' }}/>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                          <Tooltip title="Ficha clínica">
                            <IconButton size="small" onClick={() => navigate(`/patients/${p.patientId}/record`)} sx={{ color: PRIMARY }}>
                              <MedicalServicesIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Historial de citas">
                            <IconButton size="small" onClick={() => setHistoryPatient(p)}>
                              <CalendarTodayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver perfil"><IconButton size="small" onClick={() => setDrawerPatient(p)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditTarget(p)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Archivar"><IconButton size="small" color="warning" onClick={() => setDeleteTarget(p)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={sorted.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        ) : (
          <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
            {paginated.map(p => {
              const sc = speciesColor(p.species);
              return (
                <Paper elevation={0} sx={{
                  p: 2, borderRadius: 3, border: '1px solid #E2E8F0', cursor: 'pointer',
                  '&:hover': { boxShadow: 3, borderColor: PRIMARY },
                  transition: 'all 0.15s',
                }} onClick={() => setDrawerPatient(p)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar src={p.photoURL}
                      sx={{ width: 48, height: 48, bgcolor: sc.avatar, fontWeight: 700 }}>
                      {!p.photoURL && (p.name?.[0]?.toUpperCase() || <PetsIcon/>)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={700} noWrap>{p.name}</Typography>
                      <Chip label={p.species} size="small"
                        sx={{ bgcolor: sc.bg, color: sc.text, fontSize: '0.65rem', height: 18 }}/>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {p.breed} · {calcAge(p.birthDate)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }}/>
                    <Typography variant="caption" color="text.secondary" noWrap>{p.ownerName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                    {p.isHospitalized && <Chip label="Internado" size="small"
                      sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#FEF3C7', color: '#92400E' }}/>}
                    {p.alergias && <Chip label="⚠ Alergia" size="small"
                      sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#FEE2E2', color: '#991B1B' }}/>}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}
                    onClick={e => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => navigate(`/patients/${p.patientId}/record`)}
                      sx={{ color: PRIMARY }}><MedicalServicesIcon sx={{ fontSize: 16 }}/></IconButton>
                    <IconButton size="small" onClick={() => setHistoryPatient(p)}>
                      <CalendarTodayIcon sx={{ fontSize: 16 }}/>
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditTarget(p)}><EditIcon sx={{ fontSize: 16 }}/></IconButton>
                    <IconButton size="small" color="warning" onClick={() => setDeleteTarget(p)}><DeleteIcon sx={{ fontSize: 16 }}/></IconButton>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Drawer de perfil */}
      <PatientDrawer
        patient={drawerPatient}
        onClose={() => setDrawerPatient(null)}
        onEdit={p => setEditTarget(p)}
      />
      <BookingsHistoryDialog
        patient={historyPatient}
        onClose={() => setHistoryPatient(null)}
      />

      {/* Dialogo nuevo / editar */}
      <PatientOwnerDialog
        open={newOpen || !!editTarget}
        onClose={() => { setNewOpen(false); setEditTarget(null); }}
        onSaved={fetchPatients}
        initial={editTarget}
      />

      {/* Dialogo de archivar (soft delete) */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Archivar Paciente</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            El paciente será archivado, no eliminado. El historial clínico se conserva.
          </Alert>
          <Typography>
            ¿Archivar a <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
          <Button color="warning" variant="contained" onClick={handleArchive} disabled={deleting}>
            {deleting ? <CircularProgress size={20}/> : 'Archivar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Patients;

